import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Modal from "../components/ui/Modal";
import { getApiErrorMessage } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { incomingOrderService } from "../services/incomingOrderService";
import { productService } from "../services/productService";
import { profilerService } from "../services/profilerService";
import { normalizeProductsResponse } from "../utils/productManagement";
import {
  ArrowRight,
  Loader2,
  MapPin,
  Package,
  Plus,
  RefreshCw,
  Search,
  ShoppingBag,
  Store,
  Trash2,
  User,
} from "lucide-react";

const PAGE_SIZE = 10;

const pickArray = (...candidates) =>
  candidates.find((candidate) => Array.isArray(candidate)) || [];

const createEmptyLineItem = () => ({
  productId: "",
  name: "",
  quantity: "1",
  unit: "",
  unitPrice: "",
  notes: "",
});

const EMPTY_ORDER_FORM = {
  customerName: "",
  deliveryAddress: "",
  specialInstructions: "",
  estimatedTotal: "",
  currency: "NGN",
  rawNotes: "Manual order entered by seller dashboard",
  items: [createEmptyLineItem()],
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatCurrency = (amount, currency = "NGN") => {
  try {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: currency || "NGN",
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  } catch {
    return `${currency || "NGN"} ${Number(amount || 0).toLocaleString()}`;
  }
};

const formatConfidence = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  if (value >= 0 && value <= 1) {
    return `${Math.round(value * 100)}%`;
  }

  return String(value);
};

const normalizeIncomingOrderItem = (item, index) => ({
  id: item?.id || `${item?.productId || item?.name || "item"}-${index}`,
  name: item?.name || item?.product?.name || "",
  quantity: Number(item?.quantity ?? 0) || 0,
  unit: item?.unit || "",
  unitPrice: Number(item?.unitPrice ?? 0) || 0,
  notes: item?.notes || item?.note || "",
  productId: item?.productId || item?.product?.id || "",
});

const normalizeIncomingOrder = (data) => {
  const order = data?.data && !Array.isArray(data?.data) ? data.data : data;
  const profiler = order?.profiler || {};
  const seller = profiler?.seller || order?.seller || {};

  return {
    id: order?.id || order?.orderId || "",
    customerName: order?.customerName || "",
    deliveryAddress: order?.deliveryAddress || "",
    specialInstructions: order?.specialInstructions || "",
    estimatedTotal: Number(order?.estimatedTotal ?? 0) || 0,
    currency: order?.currency || "NGN",
    confidence:
      typeof order?.confidence === "number" ? order.confidence : null,
    rawNotes: order?.rawNotes || "",
    items: pickArray(order?.items, order?.orderItems).map(
      normalizeIncomingOrderItem,
    ),
    profiler,
    seller,
    createdAt: order?.createdAt || "",
    updatedAt: order?.updatedAt || "",
  };
};

const normalizeIncomingOrdersCollection = (data) => {
  const items = pickArray(
    data,
    data?.items,
    data?.orders,
    data?.results,
    data?.data,
    data?.data?.items,
    data?.data?.orders,
  ).map(normalizeIncomingOrder);

  const meta = data?.meta || data?.pagination || data?.data?.meta || {};
  const page = Number(meta?.page ?? data?.page ?? data?.currentPage ?? 1) || 1;
  const limit =
    Number(meta?.limit ?? data?.limit ?? data?.pageSize ?? PAGE_SIZE) || PAGE_SIZE;
  const totalValue = Number(meta?.total ?? data?.total ?? data?.count ?? 0);
  const hasKnownTotal = !Number.isNaN(totalValue) && totalValue > 0;
  const total = hasKnownTotal ? totalValue : items.length;
  const totalPagesValue = Number(
    meta?.totalPages ?? data?.totalPages ?? (hasKnownTotal ? Math.ceil(total / limit) : 0),
  );
  const totalPages = totalPagesValue > 0 ? totalPagesValue : 1;
  const hasNextPage = hasKnownTotal
    ? page < totalPages
    : items.length === limit;

  return {
    items,
    page,
    limit,
    total,
    totalPages,
    hasKnownTotal,
    hasNextPage,
  };
};

const hasValidationErrors = (errors) =>
  Boolean(
    errors.customerName ||
      errors.items ||
      (Array.isArray(errors.itemErrors) &&
        errors.itemErrors.some((itemError) => Object.keys(itemError || {}).length > 0)),
  );

const validateOrderForm = (formData) => {
  const errors: any = {
    customerName: "",
    items: "",
    itemErrors: [],
  };

  if (!formData.customerName.trim()) {
    errors.customerName = "Customer name is required.";
  }

  const populatedItems = formData.items.filter(
    (item) =>
      item.name.trim() ||
      item.productId ||
      item.quantity !== "" ||
      item.unit.trim() ||
      item.unitPrice !== "" ||
      item.notes.trim(),
  );

  if (populatedItems.length === 0) {
    errors.items = "Add at least one item.";
  }

  errors.itemErrors = formData.items.map((item) => {
    const itemErrors: any = {};

    if (!item.name.trim()) {
      itemErrors.name = "Item name is required.";
    }

    const quantity = Number(item.quantity);
    if (!item.quantity || Number.isNaN(quantity) || quantity <= 0) {
      itemErrors.quantity = "Quantity is required.";
    }

    const unitPrice = Number(item.unitPrice);
    if (item.unitPrice === "" || Number.isNaN(unitPrice) || unitPrice < 0) {
      itemErrors.unitPrice = "Unit price is required.";
    }

    return itemErrors;
  });

  return errors;
};

export default function IncomingOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = user?.token;

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [formData, setFormData] = useState(EMPTY_ORDER_FORM);
  const [formErrors, setFormErrors] = useState({
    customerName: "",
    items: "",
    itemErrors: [],
  });
  const [pageMessage, setPageMessage] = useState({ type: "", text: "" });

  const profileQuery = useQuery({
    queryKey: ["profiler-account-me", "incoming-orders"],
    queryFn: async () => profilerService.getMe(token),
    enabled: Boolean(token),
  });

  const currentProfiler = profileQuery.data || null;
  const seller = currentProfiler?.seller || null;
  const profilerId = currentProfiler?.id || user?.id || "";
  const sellerAccountId = currentProfiler?.seller?.id || user?.sellerAccountId || "";
  const hasSellerAccount = Boolean(sellerAccountId);

  const ordersQuery = useQuery({
    queryKey: [
      "incoming-orders",
      currentPage,
      PAGE_SIZE,
      search,
      sellerAccountId,
      profilerId,
    ],
    queryFn: async () =>
      normalizeIncomingOrdersCollection(
        await incomingOrderService.listIncomingOrders({
          page: currentPage,
          limit: PAGE_SIZE,
          search: search.trim() || undefined,
          ...(sellerAccountId
            ? { sellerAccountId }
            : profilerId
              ? { profilerId }
              : {}),
        }),
      ),
    enabled: Boolean(token) && Boolean(profilerId),
  });

  const orderDetailQuery = useQuery({
    queryKey: ["incoming-order", selectedOrderId],
    queryFn: async () =>
      normalizeIncomingOrder(
        await incomingOrderService.getIncomingOrderById(selectedOrderId),
      ),
    enabled: Boolean(token) && Boolean(selectedOrderId),
  });

  const productsLookupQuery = useQuery({
    queryKey: ["seller-products", "incoming-order-form", productSearch],
    queryFn: async () =>
      normalizeProductsResponse(
        await productService.listMyProducts({
          page: 1,
          limit: 50,
          search: productSearch.trim() || undefined,
          sortBy: "latest",
        }),
      ),
    enabled:
      Boolean(token) && Boolean(sellerAccountId) && isCreateModalOpen,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: any) =>
      incomingOrderService.createIncomingOrder(payload),
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const orderCollection =
    ordersQuery.data ||
    normalizeIncomingOrdersCollection({ items: [], limit: PAGE_SIZE, page: 1 });
  const orders = orderCollection.items;
  const productOptions = productsLookupQuery.data || [];
  const selectedOrder = orderDetailQuery.data || null;

  const calculatedTotal = useMemo(
    () =>
      formData.items.reduce((sum, item) => {
        const quantity = Number(item.quantity) || 0;
        const unitPrice = Number(item.unitPrice) || 0;
        return sum + quantity * unitPrice;
      }, 0),
    [formData.items],
  );

  const profileError = profileQuery.error
    ? getApiErrorMessage(profileQuery.error, "Could not load current seller profile.")
    : "";
  const ordersError = ordersQuery.error
    ? getApiErrorMessage(ordersQuery.error, "Could not load incoming orders.")
    : "";
  const detailError = orderDetailQuery.error
    ? getApiErrorMessage(orderDetailQuery.error, "Could not load incoming order details.")
    : "";
  const productsError = productsLookupQuery.error
    ? getApiErrorMessage(productsLookupQuery.error, "Could not load your products.")
    : "";

  const resetCreateForm = () => {
    setFormData(EMPTY_ORDER_FORM);
    setFormErrors({ customerName: "", items: "", itemErrors: [] });
    setProductSearch("");
  };

  const openCreateModal = () => {
    resetCreateForm();
    setPageMessage((prev) => (prev.type === "success" ? prev : { type: "", text: "" }));
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  };

  const handleItemChange = (index, name, value) => {
    setFormData((prev) => {
      const nextItems = prev.items.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const nextItem = { ...item, [name]: value };

        if (name === "productId") {
          const selectedProduct = productOptions.find((product) => product.id === value);
          if (selectedProduct && !item.name.trim()) {
            nextItem.name = selectedProduct.name;
          }
        }

        return nextItem;
      });

      return {
        ...prev,
        items: nextItems,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!profilerId || createMutation.isPending) {
      return;
    }

    const nextErrors = validateOrderForm(formData);
    setFormErrors(nextErrors);

    if (hasValidationErrors(nextErrors)) {
      return;
    }

    try {
      const payload = {
        profilerId,
        customerName: formData.customerName.trim(),
        deliveryAddress: formData.deliveryAddress.trim(),
        specialInstructions: formData.specialInstructions.trim(),
        estimatedTotal:
          formData.estimatedTotal === ""
            ? calculatedTotal
            : Number(formData.estimatedTotal),
        currency: formData.currency.trim() || "NGN",
        rawNotes: formData.rawNotes.trim(),
        items: formData.items.map((item) => {
          const payloadItem: any = {
            name: item.name.trim(),
            quantity: Number(item.quantity),
            unit: item.unit.trim(),
            unitPrice: Number(item.unitPrice),
            notes: item.notes.trim(),
          };

          if (item.productId) {
            payloadItem.productId = item.productId;
          }

          return payloadItem;
        }),
      };

      await createMutation.mutateAsync(payload);
      await queryClient.invalidateQueries({ queryKey: ["incoming-orders"] });
      setCurrentPage(1);
      closeCreateModal();
      setPageMessage({
        type: "success",
        text: "Incoming order created successfully.",
      });
    } catch (error) {
      setPageMessage({
        type: "error",
        text: getApiErrorMessage(error, "Could not create incoming order."),
      });
    }
  };

  if (!token) {
    return null;
  }

  if (profileQuery.isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="mx-auto animate-spin text-violet-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading incoming orders...
          </p>
        </div>
      </div>
    );
  }

  if (!hasSellerAccount) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-900">
          <Store size={40} className="mx-auto text-gray-300 dark:text-gray-700" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
            Incoming orders are unavailable
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            This screen is available only after seller provisioning. Finish
            seller onboarding first, then return once the seller account exists
            on your profiler record.
          </p>
          <button
            type="button"
            onClick={() => navigate(user?.hasTenantAccess ? "/tenant" : "/dashboard")}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
            Return <ArrowRight size={15} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <section className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-violet-500">
              Incoming Order Management
            </p>
            <h1 className="mt-2 text-3xl font-black text-gray-900 dark:text-gray-100">
              Unified incoming orders
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
              This list is backed by the incoming-orders endpoint and stays
              scoped to the current seller account. Telegram-generated orders
              and manual seller-created orders appear in the same flow.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => ordersQuery.refetch()}
              disabled={ordersQuery.isFetching}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800">
              {ordersQuery.isFetching ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <RefreshCw size={15} />
              )}
              Refresh
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
              <Plus size={15} /> Create manual order
            </button>
          </div>
        </div>
      </section>

      {(profileError || ordersError || pageMessage.text) && (
        <div
          className={`rounded-2xl border p-4 text-sm ${pageMessage.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
            }`}>
          {pageMessage.text || ordersError || profileError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ScopeCard
          label="Seller scope"
          value={seller?.shopName || "Current seller"}
          hint="Requests use sellerAccountId when available"
          icon={Store}
        />
        <ScopeCard
          label="Current market"
          value={seller?.market?.name || user?.marketName || "Not assigned"}
          hint={seller?.market?.city || "Seller market from current profile"}
          icon={MapPin}
        />
        <ScopeCard
          label="Orders on page"
          value={String(orders.length)}
          hint={
            orderCollection.hasKnownTotal
              ? `${orderCollection.total} total orders returned by the backend`
              : "Total count not returned by the backend"
          }
          icon={ShoppingBag}
        />
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative flex-1 max-w-2xl">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, address, notes, or order details"
              className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Page {currentPage}
            {orderCollection.hasKnownTotal ? ` of ${orderCollection.totalPages}` : ""}
          </p>
        </div>

        {ordersQuery.isLoading ? (
          <div className="py-16 text-center">
            <Loader2 size={32} className="mx-auto animate-spin text-violet-500" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Loading incoming orders...
            </p>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center">
            <ShoppingBag size={36} className="mx-auto text-gray-300 dark:text-gray-700" />
            <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              No incoming orders returned
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              The current backend response did not return any Telegram-generated
              or manual orders for this seller scope.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {orders.map((order) => (
              <article
                key={order.id}
                className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-950">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-3 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {order.customerName || "Unnamed customer"}
                      </h3>
                      {typeof order.confidence === "number" && (
                        <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300">
                          Confidence {formatConfidence(order.confidence)}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <InlineInfo
                        icon={MapPin}
                        label="Delivery Address"
                        value={order.deliveryAddress || "Not provided"}
                      />
                      <InlineInfo
                        icon={Store}
                        label="Seller"
                        value={order.seller?.shopName || seller?.shopName || "Current seller"}
                      />
                      <InlineInfo
                        icon={Package}
                        label="Items"
                        value={`${order.items.length} item${order.items.length === 1 ? "" : "s"}`}
                      />
                      <InlineInfo
                        icon={User}
                        label="Updated"
                        value={formatDateTime(order.updatedAt || order.createdAt)}
                      />
                    </div>

                    {order.specialInstructions && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                        {order.specialInstructions}
                      </div>
                    )}

                    {order.rawNotes && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {order.rawNotes}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {order.items.slice(0, 3).map((item) => (
                        <span
                          key={item.id}
                          className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 dark:bg-gray-900 dark:text-gray-200">
                          {item.name} x{item.quantity}
                        </span>
                      ))}
                      {order.items.length > 3 && (
                        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 dark:bg-gray-900 dark:text-gray-200">
                          +{order.items.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(order.estimatedTotal, order.currency)}
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelectedOrderId(order.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
                      View details <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {!ordersQuery.isLoading && orders.length > 0 && (
          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 text-sm dark:border-gray-800 dark:bg-gray-950 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-gray-500 dark:text-gray-400">
              Showing page {currentPage}
              {orderCollection.hasKnownTotal ? ` of ${orderCollection.totalPages}` : ""}.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage === 1}
                className="rounded-xl border border-gray-200 px-3 py-2 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) =>
                    orderCollection.hasNextPage ? page + 1 : page,
                  )
                }
                disabled={!orderCollection.hasNextPage}
                className="rounded-xl border border-gray-200 px-3 py-2 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <Modal
        isOpen={Boolean(selectedOrderId)}
        onClose={() => setSelectedOrderId("")}
        title="Incoming order details"
        size="lg">
        {orderDetailQuery.isLoading ? (
          <div className="py-12 text-center">
            <Loader2 size={28} className="mx-auto animate-spin text-violet-500" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Loading order details...
            </p>
          </div>
        ) : detailError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
            {detailError}
          </div>
        ) : selectedOrder ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <DetailCard
                label="Customer"
                value={selectedOrder.customerName || "Unnamed customer"}
              />
              <DetailCard
                label="Estimated Total"
                value={formatCurrency(
                  selectedOrder.estimatedTotal,
                  selectedOrder.currency,
                )}
              />
              <DetailCard
                label="Confidence"
                value={
                  typeof selectedOrder.confidence === "number"
                    ? formatConfidence(selectedOrder.confidence)
                    : "-"
                }
              />
              <DetailCard
                label="Updated"
                value={formatDateTime(
                  selectedOrder.updatedAt || selectedOrder.createdAt,
                )}
              />
            </div>

            <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <DetailSection
                title="Customer details"
                rows={[
                  {
                    label: "Delivery address",
                    value: selectedOrder.deliveryAddress || "Not provided",
                  },
                  {
                    label: "Special instructions",
                    value: selectedOrder.specialInstructions || "Not provided",
                  },
                  {
                    label: "Raw notes",
                    value: selectedOrder.rawNotes || "Not provided",
                  },
                ]}
              />
              <DetailSection
                title="Seller details"
                rows={[
                  {
                    label: "Shop name",
                    value:
                      selectedOrder.seller?.shopName || seller?.shopName || "Current seller",
                  },
                  {
                    label: "Store address",
                    value:
                      selectedOrder.seller?.storeAddress || seller?.storeAddress || "Not provided",
                  },
                  {
                    label: "Profiler",
                    value: selectedOrder.profiler?.id || profilerId || "-",
                  },
                ]}
              />
            </section>

            <section className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Line items
              </h3>
              <div className="mt-4 space-y-3">
                {selectedOrder.items.map((item) => (
                  <article
                    key={item.id}
                    className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {item.name}
                        </p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {item.quantity} {item.unit || "unit"}
                          {item.notes ? ` • ${item.notes}` : ""}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {formatCurrency(item.unitPrice, selectedOrder.currency)}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Create manual incoming order"
        size="xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {pageMessage.type === "error" && isCreateModalOpen && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
              {pageMessage.text}
            </div>
          )}

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              label="Customer Name"
              error={formErrors.customerName}
              required
            >
              <input
                value={formData.customerName}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    customerName: event.target.value,
                  }))
                }
                className={inputClass(Boolean(formErrors.customerName))}
                placeholder="Adaeze Okonkwo"
              />
            </FormField>

            <FormField label="Currency">
              <input
                value={formData.currency}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    currency: event.target.value,
                  }))
                }
                className={inputClass(false)}
                placeholder="NGN"
              />
            </FormField>

            <FormField label="Delivery Address">
              <input
                value={formData.deliveryAddress}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    deliveryAddress: event.target.value,
                  }))
                }
                className={inputClass(false)}
                placeholder="14 Balogun Street, Lagos Island"
              />
            </FormField>

            <FormField
              label="Estimated Total"
              helper="If left blank, the form uses the calculated item total."
            >
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.estimatedTotal}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    estimatedTotal: event.target.value,
                  }))
                }
                className={inputClass(false)}
                placeholder={String(calculatedTotal)}
              />
            </FormField>
          </section>

          <FormField label="Special Instructions">
            <textarea
              value={formData.specialInstructions}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  specialInstructions: event.target.value,
                }))
              }
              className={textareaClass(false)}
              placeholder="Call before dispatch"
            />
          </FormField>

          <FormField label="Raw Notes">
            <textarea
              value={formData.rawNotes}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  rawNotes: event.target.value,
                }))
              }
              className={textareaClass(false)}
              placeholder="Manual order entered by seller dashboard"
            />
          </FormField>

          <section className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Line items
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Link a line item to one of your products if you want, or leave it manual.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    items: [...prev.items, createEmptyLineItem()],
                  }))
                }
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800">
                <Plus size={15} /> Add item
              </button>
            </div>

            {formErrors.items && (
              <p className="mt-4 text-sm font-semibold text-rose-600 dark:text-rose-300">
                {formErrors.items}
              </p>
            )}

            <div className="mt-5 space-y-5">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                <FormField
                  label="Search my products"
                  helper="This lookup uses GET /product-management/me/products and is optional for each line item."
                >
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                      className={inputClass(false, true)}
                      placeholder="Search your products"
                    />
                  </div>
                </FormField>

                {productsLookupQuery.isLoading && (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    Loading your products...
                  </p>
                )}

                {productsError && (
                  <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">
                    {productsError}
                  </p>
                )}
              </div>

              {formData.items.map((item, index) => {
                const itemErrors = formErrors.itemErrors[index] || {};

                return (
                  <article
                    key={`item-${index}`}
                    className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Item {index + 1}
                        </p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Product link is optional. Name, quantity, and unit price are required.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            items:
                              prev.items.length === 1
                                ? prev.items
                                : prev.items.filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                        disabled={formData.items.length === 1}
                        className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300 dark:hover:bg-rose-950/40">
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <FormField label="Linked Product">
                        <select
                          value={item.productId}
                          onChange={(event) =>
                            handleItemChange(index, "productId", event.target.value)
                          }
                          className={inputClass(false)}>
                          <option value="">No linked product</option>
                          {productOptions.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))}
                        </select>
                      </FormField>

                      <FormField
                        label="Item Name"
                        error={itemErrors.name}
                        required
                      >
                        <input
                          value={item.name}
                          onChange={(event) =>
                            handleItemChange(index, "name", event.target.value)
                          }
                          className={inputClass(Boolean(itemErrors.name))}
                          placeholder="50kg Bag of Rice"
                        />
                      </FormField>

                      <FormField label="Unit">
                        <input
                          value={item.unit}
                          onChange={(event) =>
                            handleItemChange(index, "unit", event.target.value)
                          }
                          className={inputClass(false)}
                          placeholder="bag"
                        />
                      </FormField>

                      <FormField
                        label="Quantity"
                        error={itemErrors.quantity}
                        required
                      >
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={item.quantity}
                          onChange={(event) =>
                            handleItemChange(index, "quantity", event.target.value)
                          }
                          className={inputClass(Boolean(itemErrors.quantity))}
                          placeholder="2"
                        />
                      </FormField>

                      <FormField
                        label="Unit Price"
                        error={itemErrors.unitPrice}
                        required
                      >
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(event) =>
                            handleItemChange(index, "unitPrice", event.target.value)
                          }
                          className={inputClass(Boolean(itemErrors.unitPrice))}
                          placeholder="18000"
                        />
                      </FormField>

                      <FormField label="Item Notes">
                        <input
                          value={item.notes}
                          onChange={(event) =>
                            handleItemChange(index, "notes", event.target.value)
                          }
                          className={inputClass(false)}
                          placeholder="Urgent delivery"
                        />
                      </FormField>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-950/30">
            <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">
              Calculated total
            </p>
            <p className="mt-2 text-2xl font-bold text-violet-700 dark:text-violet-200">
              {formatCurrency(calculatedTotal, formData.currency || "NGN")}
            </p>
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeCreateModal}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
              {createMutation.isPending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Plus size={15} />
              )}
              Create incoming order
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function ScopeCard({ label, value, hint, icon: Icon }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
            {label}
          </p>
          <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {value}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300">
          <Icon size={18} />
        </div>
      </div>
    </article>
  );
}

function InlineInfo({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
        <Icon size={13} />
        {label}
      </div>
      <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">{value}</p>
    </div>
  );
}

function DetailCard({ label, value }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {value}
      </p>
    </article>
  );
}

function DetailSection({ title, rows }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {title}
      </h3>
      <div className="mt-4 space-y-3">
        {rows.map((row) => (
          <div key={row.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
              {row.label}
            </p>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              {row.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FormField({ label, helper = "", error = "", required = false, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
      {helper && !error && (
        <span className="mt-2 block text-xs text-gray-500 dark:text-gray-400">
          {helper}
        </span>
      )}
      {error && (
        <span className="mt-2 block text-xs font-semibold text-rose-600 dark:text-rose-300">
          {error}
        </span>
      )}
    </label>
  );
}

function inputClass(hasError, withIcon = false) {
  return `w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:ring-2 focus:ring-violet-500/20 dark:bg-gray-950 dark:text-gray-100 ${withIcon ? "pl-11" : ""} ${hasError
      ? "border-rose-300 focus:border-rose-400 dark:border-rose-800"
      : "border-gray-200 focus:border-violet-500 dark:border-gray-700"
    }`;
}

function textareaClass(hasError) {
  return `min-h-24 w-full rounded-xl border bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:ring-2 focus:ring-violet-500/20 dark:bg-gray-950 dark:text-gray-100 ${hasError
      ? "border-rose-300 focus:border-rose-400 dark:border-rose-800"
      : "border-gray-200 focus:border-violet-500 dark:border-gray-700"
    }`;
}